import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Response } from '@angular/http';
import { NavigationExtras } from '@angular/router/src/router';
import { AngularFireAuth } from 'angularfire2/auth';
import * as firebase from 'firebase/app';
import * as jwt_decode from 'jwt-decode';
import { Observable, Observer } from 'rxjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subscription } from 'rxjs/Subscription';
import { StateService } from '../../state/state.service';
import { LoginDaoService } from '../dao-service/login-dao.service';
import { AuthError } from './auth-error';
import { LoginCommand, LogoutCommand, MarkAuthAsInitializedCommand } from '../../state/auth/auth-command-handler.service';
import { Reauthenticator } from '../reauthenticator';
import { environment } from '../../../environments/environment';
import { isNullOrUndefined } from 'util';

@Injectable()
export class AuthService implements Reauthenticator {

  private isRedirecting = false;
  private loggingOut = false;
  private firebaseUserSubject: BehaviorSubject<firebase.User> = new BehaviorSubject(null);
  private authErrorSubject: BehaviorSubject<AuthError> = new BehaviorSubject(null);
  private subscription: Subscription;

  public readonly error$: Observable<AuthError> = this.authErrorSubject.asObservable();
  public readonly firebaseUser$: Observable<firebase.User> = this.firebaseUserSubject.asObservable();

  get firebaseUser(): firebase.User {
    return this.firebaseUserSubject.getValue();
  }

  constructor(
    private af: AngularFireAuth,
    private router: Router,
    private state: StateService,
    private dao: LoginDaoService,
  ) { }

  init() {
    if (this.subscription) {
      return;
    }

    this.subscription = this.af.authState.subscribe(user => {
      this.firebaseUserSubject.next(user);
      if (!user) {
        this.ensureUserSignedOut(user);
        this.state.execute(new MarkAuthAsInitializedCommand());

        return;
      }

      this.loginUser(user);
    });
  }

  register(email: string, password: string): void {
    this.init();

    this.af.auth.createUserWithEmailAndPassword(email, password)
      .then((user_cd) => user_cd.user.sendEmailVerification())
      .then(() => this.redirect(['verify-account']))
      .catch(e => {
        this.authErrorSubject.next(new AuthError(e));
      });
  }

  loginAnonymus(): void {
    this.init();

    this.af.auth.signInAnonymously().then(authUser => {
    }, e => {
      this.authErrorSubject.next(new AuthError(e));
    });
  }

  loginEmail(email: string, password: string): void {
    this.init();

    this.af.auth.signInWithEmailAndPassword(email, password).then(authUser => {
    }, e => {
      this.authErrorSubject.next(new AuthError(e));
    });
  }

  registerAnonymus(email: string, password: string): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      const cred = firebase.auth.EmailAuthProvider.credential(email, password);
      this.af.auth.currentUser.linkWithCredential(cred).then(authUser => {
        observer.next(authUser);
        observer.complete();
      }, e => {
        this.authErrorSubject.next(new AuthError(e));
        observer.error(e);
      });
    });
  }

  reauthenticateWithCredential(email: string, password: string): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      var cred = firebase.auth.EmailAuthProvider.credential(email, password);
      this.af.auth.currentUser.reauthenticateWithCredential(cred).then(authUser => {
        this.loginUser(this.af.auth.currentUser);
        observer.next(authUser);
        observer.complete();
      }, e => {
        this.authErrorSubject.next(new AuthError(e));
        observer.error(e);
      });
    });
  }

  sendPasswordResetEmail(email: string) {
    return this.af.auth.sendPasswordResetEmail(email).then(authUser => {
      this.removeError();
      this.redirect(['reset-success']);
    }, e => {
      this.authErrorSubject.next(new AuthError(e));
    });

  }

  loginGoogle(): void {
    this.init();

    this.af.auth.signInWithPopup(new firebase.auth.GoogleAuthProvider()).then(authUser => {
    });
  }

  logout(): void {

    this.state.execute(new LogoutCommand());
    this.af.auth.signOut().then(() => {
      this.redirect(['login']);

    }).catch(error => {
      console.log(error);
      return Observable.of(null);
    });
  }

  reauthenticate(response: Response) {
    if (!isNullOrUndefined(response) && response.status === 403) {
      setTimeout(() => {
        this.logout();
      }, 0);

      return Observable.of(false);
    }

    return this.af.authState
      .first()
      .switchMap(user => user ?
        Observable.fromPromise(user.getIdToken())
          .switchMap(token => this.onFirebaseCredentials(user, token)) :
        Observable.of(null))
      .map(data => data !== null);
  }

  removeError() {
    this.authErrorSubject.next(null);
  }

  private loginUser(user: firebase.User): void {
    user.getIdToken().then(token => this.onFirebaseCredentials(user, token).subscribe());
  }

  private ensureUserSignedOut(user: firebase.User = null): void {
    if (this.state.snapshot.auth.isAuthenticated) {
      this.logout();
    }
  }

  private onFirebaseCredentials(user: firebase.User, token: string) {

    const decodedToken = jwt_decode(token);


    let acl;
    let userTypeIdParam;
    if (user.isAnonymous) {
      acl = '';
      userTypeIdParam = '';
    } else {
      acl = decodedToken.acl;
      switch (acl[0]) {
        case 'adm':
          userTypeIdParam = '';
          break;
        case 'agn':
          userTypeIdParam = 'agent_id';
          break;
        case 'shp':
          userTypeIdParam = 'shop_id';
          break;
        case 'lab':
          userTypeIdParam = 'lab_id';
          break;
        case 'mshp':
          userTypeIdParam = 'mshp_id';
          break;
        case 'cov':
          userTypeIdParam = '';
          break;
        case 'cli':
          userTypeIdParam = '';
          break;
      }
    }

    console.log(decodedToken);

    this.state.execute(new LoginCommand(
      decodedToken.user_id,
      token,
      user.email,
      decodedToken.acl,
      decodedToken[userTypeIdParam]
    ));
    this.state.execute(new MarkAuthAsInitializedCommand());

    return Observable.of(true);
  }

  private redirect(commands: any[], extras?: NavigationExtras) {
    if (this.isRedirecting) {
      return;
    }
    this.isRedirecting = true;
    this.router.navigate(commands, extras).then(() => this.isRedirecting = false);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/auth/auth.service.ts